import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  linkedSignal,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';

import { BehaviorSubject, switchMap, forkJoin, firstValueFrom } from 'rxjs';
import {
  API,
  BookingDTO,
  CompanyDTO,
  GroupMediumDTO,
  InventoryMarkerFullDTO,
  MediumDTO,
  MediumFolderDTO,
  MediumFullDTO,
  MediumGroupFullDTO,
  mm_Medium_retrieveAll_order,
  NamedIdentityDTO,
  Order,
  Pagination,
  ResolutionDTO,
  SingleFileUpload,
  TemplateGroupDTO,
  TemplateSourceDTO,
} from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { SbModalState } from '../../../services/gui/modal/modal-state.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { CompaniesComponent } from '../../company/companies/companies.component';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import { MediumUploadDTOAndTags } from '../../../models/taggable.model';
import { MediumTemplatePreviewComponent } from '../medium-template-preview/medium-template-preview.component';
import { NavigationService } from '@ngx-seatback/utils';
import { QueryParamService } from 'src/app/services/lists/query-params';
import { TemplateGroupsComponent } from '../templateGroup/template-groups.component';
import { TemplateSourceComponent } from '../template-source/template-source.component';
import { DragItemStyle, Mutable, OrderOption, SbSplitButton, SelectionState } from '../../../services/global/global-interfaces';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { UserService } from '../../../services/user.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { MultiSelectButtonAction } from '../../../components/table-multi-select-button/table-multi-select-button.component';
import { SbSplitButtonItem, SearchOptions, SearchType } from '@ngx-seatback/controls';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { MediumViewerComponent } from '../../../components/controls/medium-viewer/medium-viewer.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RequestClass } from '../../../services/global/RequestClass';

export type MediumUploadDTO = Mutable<MediumFullDTO>;

type SplitButtonAction = 'delete' | 'edit' | 'details' | 'lock' | 'download';
type MediumUploadDTOAndTagsAndButtons = MediumUploadDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-medium-explorer',
  templateUrl: './medium-explorer.component.html',
  styleUrls: ['./medium-explorer.component.scss'],
  animations: [
    trigger('popup', [
      transition('void => *', [style({ transform: 'scale3d(.3, .3, .3)' }), animate(100)]),
      transition('* => void', [animate(100, style({ transform: 'scale3d(.0, .0, .0)' }))]),
    ]),
    trigger('slideInOut', [
      transition(':enter', [style({ transform: 'translateX(+100%)' }), animate('200ms ease-in', style({ transform: 'translateX(0%)' }))]),
      transition(':leave', [animate('200ms ease-in', style({ transform: 'translateX(+100%)' }))]),
    ]),
    trigger('fadeAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate(400)]),
      transition(':leave', animate(400, style({ opacity: 0 }))),
    ]),
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediumExplorerComponent extends RequestClass implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);
  /* Upload */
  @ViewChild('filePicker') filePicker: ElementRef;
  @ViewChild('fileReplacePicker') fileReplacePicker: ElementRef;

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  protected readonly mm_Medium_retrieveAll_order = mm_Medium_retrieveAll_order;
  protected readonly Array = Array;
  protected readonly JSON = JSON;

  /* Modal */
  @Input() isInModalContext: any;
  @Input() modalType: any;
  @Input() excludeMedia: GroupMediumDTO[];
  @Input() customer: NamedIdentityDTO;

  // renamed input to avoid race condition with component input binding
  @Input() set selectedResolution(val: ResolutionDTO) {
    this.resolution = val;
  }

  resolution: ResolutionDTO;
  resolutionDisabled: boolean;
  startSearch: boolean = false;

  previewMedium: MediumUploadDTOAndTagsAndButtons;
  lastEditedMedium: MediumUploadDTOAndTagsAndButtons;
  medias: MediumUploadDTOAndTagsAndButtons[];
  selectedMedias: Map<number, MediumUploadDTOAndTagsAndButtons>;

  /* PAGINATION */
  exchangeMedium: MediumDTO;
  locked: boolean;
  mediumGroup: boolean;
  debounceTimer: ReturnType<typeof setTimeout>;
  resolutionOptions: ResolutionDTO[] = [];
  booking: BookingDTO;
  showSideBar: boolean;
  selectedCompany: CompanyDTO | null = null;
  preselectedCompany: CompanyDTO;
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  pagination: BehaviorSubject<Pagination> | undefined;

  allOrderOptions: OrderOption<mm_Medium_retrieveAll_order>[] = [
    {
      value: mm_Medium_retrieveAll_order.CREATED_AT,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.createdAt'),
    },
    {
      value: mm_Medium_retrieveAll_order.ID,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.id'),
    },
    {
      value: mm_Medium_retrieveAll_order.NAME,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.name'),
    },
    {
      value: mm_Medium_retrieveAll_order.DURATION,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.duration'),
    },
    {
      value: mm_Medium_retrieveAll_order.COMPANY,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.company'),
    },
  ] as const;

  /* dragging / file dummy */
  dragging: MediumUploadDTOAndTagsAndButtons;
  dragItemStyle: DragItemStyle;
  // folder
  @ViewChild('folderInput') folderInput: ElementRef;
  folderPagination: BehaviorSubject<Pagination>;
  folderString: string;
  folderSearch: string;
  exactMatchFolder: SearchOptions = 'contains';
  mediumFolders: MediumFolderDTO[];
  selectedFolder: MediumFolderDTO;
  folderDragoverId: number | null;
  folderName: string;
  folderCreationInProgress: boolean = false;
  folderCreationMode: boolean;
  folderView: boolean = false;

  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  orderOption = signal<OrderOption<mm_Medium_retrieveAll_order>>(this.allOrderOptions[0]);
  filterPanelOrderOption = linkedSignal<OrderOption<mm_Medium_retrieveAll_order>, OrderOption<mm_Medium_retrieveAll_order>>({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });

  mediumId: number | null;
  selectionState: SelectionState = 'none';
  animationOptions: any = {
    autoplay: true,
    loop: true,
  };

  constructor(
    protected readonly api: API,
    private readonly queryParamService: QueryParamService,
    private readonly modalState: SbModalState,
    private readonly state: SbModalState,
    public readonly errorHandler: ErrorService,
    public readonly modal: ModalService,
    public readonly ngbModal: NgbModal,
    public readonly util: UtilService,
    private readonly translate: TranslateService,
    public readonly tagService: TagService,
    private readonly navService: NavigationService,
    private readonly userService: UserService,
    private readonly toaster: ToasterService,
    private readonly element: ElementRef,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
    this.resolutionDisabled = false;
    this.previewMedium = undefined;
    this.lastEditedMedium = undefined;
    this.medias = [];
    this.selectedMedias = new Map<number, MediumUploadDTOAndTags>();
    this.exchangeMedium = undefined;
    this.resetOrderOption();
    this.selectedFolder = null;
    this.locked = undefined;
    this.mediumGroup = undefined;
    this.debounceTimer = null;
    this.resolution = undefined;
    this.resolutionOptions = [];
    this.booking = undefined;
    this.showSideBar = false;
    this.preselectedCompany = null;
    this.folderPagination = new BehaviorSubject<Pagination>({
      page: 1,
      count: 99,
      pages: -1,
      resultcount: -1,
    });
  }

  ngOnInit() {
    this.getFolders();
    const mediumID = +this.queryParamService.getQueryParam('mediumID');
    this.mediumId = isNaN(mediumID) ? null : mediumID;
    if (this.customer) {
      this.selectedCompany = this.customer as unknown as CompanyDTO;
      this.preselectedCompany = this.customer as unknown as CompanyDTO;
    } else {
      this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
        if (company) {
          this.selectedCompany = company;
          this.preselectedCompany = company;
          this.requestDebounced();
        }
      });
    }
    if (this.isInModalContext) {
      this.filterConfig = { ...this.filterConfig, ignoreQueryParams: true };
      this.booking = this.state.options.get(this.isInModalContext).payload?.booking;
      this.startSearch = this.state.options.get(this.isInModalContext).payload?.startSearch
        ? this.state.options.get(this.isInModalContext).payload?.startSearch
        : false;
      if (this.booking) {
        this.resolution = this.booking.marker.resolution;
        this.resolutionDisabled = true;
      } else if (this.resolution !== undefined) {
        // check works since inputs are set at onInit
        this.resolutionDisabled = true;
      }
    }

    this.api
      .ds_rm_Inventory_Markers()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (markerData: InventoryMarkerFullDTO[]) => {
          for (const markerElement of markerData) {
            this.resolutionOptions.push(markerElement.resolution);
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ value: mm_Medium_retrieveAll_order.CREATED_AT, direction: Order.desc });
  }

  parsePreviewMedium() {
    const mediumID = +this.queryParamService.getQueryParam('mediumID');
    const index = this.medias.findIndex(x => x.id === mediumID);
    if (index !== -1) {
      this.previewMedium = this.medias[index];
      this.mediumToPreview(this.previewMedium);
      this.queryParamService.clearQueryParam('mediumID');
    }
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
    if (!this.showSideBar) {
      this.previewMedium = null;
    }
  }

  closeSideBar() {
    if (this.previewMedium) {
      this.previewMedium = null;
    }
    this.showSideBar = false;
  }

  getOrder(): { by: mm_Medium_retrieveAll_order; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value) {
      this.resetOrderOption();
      return {
        by: mm_Medium_retrieveAll_order.CREATED_AT,
        direction: Order.desc,
      };
    }
    // if value equals column title
    switch (currentOrderOption.value) {
      case mm_Medium_retrieveAll_order.NAME:
        return { by: mm_Medium_retrieveAll_order.NAME, direction: currentOrderOption.direction };
      case mm_Medium_retrieveAll_order.ID:
        return { by: mm_Medium_retrieveAll_order.ID, direction: currentOrderOption.direction };
      case mm_Medium_retrieveAll_order.DURATION:
        return { by: mm_Medium_retrieveAll_order.DURATION, direction: currentOrderOption.direction };
      case mm_Medium_retrieveAll_order.COMPANY:
        return { by: mm_Medium_retrieveAll_order.COMPANY, direction: currentOrderOption.direction };
      case mm_Medium_retrieveAll_order.CREATED_AT:
      default:
        return { by: mm_Medium_retrieveAll_order.CREATED_AT, direction: currentOrderOption.direction };
    }
  }

  selectCompany(medium: MediumFullDTO) {
    this.modal
      .showNavigationModal({
        entryComponent: { value: CompaniesComponent, data: { toggleContext: 'hasMedia' } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: CompanyDTO) => {
        if (result) {
          if (!result.id) {
            medium.company = null;
          } else {
            medium.company = result;
            this.api
              .mm_Medium_edit(medium)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: (response: MediumFullDTO) => {
                  medium.company = response.company;
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          }
        } else {
          medium.company = null;
        }
      })
      .catch(() => {});
  }

  downloadMotive(medium: MediumDTO) {
    window.open(medium.publicUrl, '_blank');
  }

  showTemplatePreview(medium: MediumFullDTO) {
    this.api
      .mm_templating_provider_TemplateSource_retrieveAll({
        template_group_id: [medium.templateGroup.id],
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((sources: TemplateSourceDTO[]) => {
        this.modal
          .showNavigationModal({
            entryComponent: { value: TemplateSourceComponent, data: { templateSources: sources } },
            title: null,
            modalOptions: { windowClass: 'bigXL' },
          })
          .then(templateResult => {
            if (templateResult) {
              const source = templateResult as TemplateSourceDTO;
              this.modal
                .showNavigationModal({
                  entryComponent: {
                    value: MediumTemplatePreviewComponent,
                    data: { selectedMedium: medium, templateSource: source },
                  },
                  title: null,
                  modalOptions: { windowClass: 'bigXL' },
                })
                .then(() => {})
                .catch(() => {});
            }
          })
          .catch(() => {});
      });
  }

  selectTemplateGroup(medium: MediumFullDTO) {
    this.modal
      .showNavigationModal({
        entryComponent: { value: TemplateGroupsComponent, data: { selectedMedium: medium?.templateGroup } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then(result => {
        if (result) {
          medium.templateGroup = result as TemplateGroupDTO;
          this.api
            .mm_Medium_edit(medium)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe(() => {});
        }
      })
      .catch(() => {});
  }

  deleteTemplateGroup(medium: MediumFullDTO) {
    if (medium) {
      medium.templateGroup = null;
      this.api
        .mm_Medium_edit(medium)
        .pipe(take(1))
        .subscribe(() => {});
    }
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .mm_Medium_retrieveAll(
        {
          id: this.mediumId ? [this.mediumId] : null,
          width: this.resolution?.width ? [this.resolution.width] : null,
          height: this.resolution?.height ? [this.resolution.height] : null,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          company: this.selectedCompany?.id ? [this.selectedCompany.id] : null,
          locked: this.locked,
          folder_id: this.folderView && this.selectedFolder ? this.selectedFolder.id : null,
          hasFolder: this.folderView ? !!this.selectedFolder : null,
          hasMediumGroup: this.mediumGroup,
          order: this.getOrder(),
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(media => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Medium, media)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: data => {
          this.mediumId = null;
          /*Filter by excludeMedia*/
          if (this.excludeMedia) {
            const nu: MediumFullDTO[] = [];
            for (const old of data) {
              let include: boolean = true;
              for (const daNew of this.excludeMedia) {
                if (daNew.id === old.id) {
                  include = false;
                  break;
                }
              }
              if (include) {
                nu.push(old);
              } else {
                old['excluded'] = true;
                nu.push(old);
              }
            }
            this.medias = nu;
          } else if (!this.lastEditedMedium) {
            this.medias = data;
          } else {
            this.medias = this.orderArrayOnElement(this.lastEditedMedium, data);
            this.lastEditedMedium = null;
          }
          this.parsePreviewMedium();
          this.medias = this.buildSplitButtons(this.medias);

          this.cdRef.detectChanges();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<
    T extends {
      id: any;
      locked: boolean;
    },
  >(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          action: 'details',
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          action: 'edit',
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_show])) {
        item.sbButtons.push({
          icon: 'download',
          tooltip: 'lang.globals.download',
          action: 'download',
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_lock])) {
        item.sbButtons.push({
          icon: 'lockClosed',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'lock',
          disabled: item.locked,
          tooltip: 'lang.mediumExplorer.setReady',
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          disabled: item.locked,
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(medium: MediumUploadDTOAndTagsAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'lock':
        this.lockMedium(medium);
        break;
      case 'details':
        // preview medium in modal
        if (medium?.id) {
          const modal = this.ngbModal.open(MediumViewerComponent, { size: 'lg' });
          (modal.componentInstance as MediumViewerComponent).medium = medium;
          (modal.componentInstance as MediumViewerComponent).modalRef = modal;
        }
        break;
      case 'edit':
        this.mediumToPreview(medium);
        break;
      case 'delete':
        this.deleteMedium(medium);
        break;
      case 'download':
        this.downloadMotive(medium);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  setToOrderAndRefresh(pValue: mm_Medium_retrieveAll_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  setToOrderFilterPanel(pValue: mm_Medium_retrieveAll_order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.value = pValue;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  setOrderDirectionFilterPanel(value: Order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.direction = value;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  private setToOrderOption(pValue: mm_Medium_retrieveAll_order) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...this.allOrderOptions.find(option => (option.value = pValue)) });
    }
  }

  checkFileValid(f: File) {
    return !(!f.type || f.type.length < 2);
  }

  openFilePicker() {
    this.filePicker.nativeElement.click();
  }

  openFileReplacePicker(medium: MediumDTO) {
    this.exchangeMedium = medium;
    this.fileReplacePicker.nativeElement.click();
  }

  filePicked() {
    const files = this.filePicker.nativeElement.files;
    this.handleFileUpload(files);
    this.filePicker.nativeElement.value = '';
  }

  replacementFilePicked() {
    if (this.exchangeMedium) {
      const medium = this.exchangeMedium;
      const files = this.fileReplacePicker.nativeElement.files;
      if (files && files.length === 1) {
        this.replaceMediumFile(medium, files[0])
          .then(() => {})
          .catch(error => {
            this.errorHandler.errorHandler(error);
          })
          .finally(() => {
            this.resetMediumExchange();
          });
      } else {
        this.resetMediumExchange();
      }
    }
  }

  resetFileUpload() {
    this.filePicker.nativeElement.value = '';
  }

  resetMediumExchange() {
    this.fileReplacePicker.nativeElement.value = '';
    this.exchangeMedium = null;
  }

  dropHandler(event) {
    event.stopPropagation();
    event.preventDefault();
    const uploadFiles = [];
    if (event.dataTransfer.items) {
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        // necessary for file transfer
        if (event.dataTransfer.items[i].kind === 'file') {
          const file = event.dataTransfer.items[i].getAsFile();
          uploadFiles.push(file);
        }
      }
    } else {
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        // necessary for file transfer
        const f = event.dataTransfer.files[i];
        uploadFiles.push(f);
      }
    }

    this.handleFileUpload(uploadFiles);
  }

  handleFileUpload(files: File[]) {
    if (!files || files.length <= 0) {
      this.resetFileUpload();
      return;
    }

    const uploadFiles: File[] = [];
    for (const f of files) {
      if (this.checkFileValid(f)) {
        uploadFiles.push(f);
      }
    }

    if (uploadFiles.length <= 0) {
      return;
    }

    let promise: Promise<unknown>;
    if (uploadFiles.length === 1) {
      promise = this.createMediumByFile(uploadFiles[0]);
    } else {
      promise = this.createMultipleMediumsByFiles(uploadFiles);
    }
    promise
      .catch(error => {
        this.errorHandler.errorHandler(error);
      })
      .finally(() => {
        this.resetFileUpload();
      });
  }

  createMultipleMediumsByFiles(files: File[]): Promise<void> {
    return new Promise(resolve => {
      const filesCopy: File[] = [];
      for (const file of files) {
        filesCopy.push(file);
      }
      this.createMultipleMediumsByFilesImpl(filesCopy).then(() => resolve());
    });
  }

  createMediumByFile(file: File): Promise<MediumUploadDTO> {
    return new Promise((resolve, reject) => {
      let medium: Mutable<MediumFullDTO> = new MediumFullDTO();
      medium.name = file.name;
      medium.uploading = true;
      medium.processing = undefined;
      if (this.preselectedCompany) {
        medium.company = this.preselectedCompany;
      }
      if (this.selectedFolder) {
        medium.folder = this.selectedFolder;
      }
      // remove current displayed elements for uploading elements
      while (this.medias.length >= this.pagination.getValue().count) {
        this.medias.pop();
      }

      firstValueFrom(this.api.mm_Medium_create(medium))
        .then((result: MediumFullDTO) => {
          medium.id = result.id;
          return this.uploadMediumFile(medium, file);
        })
        .then((upload: MediumUploadDTO) => {
          if (upload) {
            medium.processing = true;
            medium.uploading = undefined;
            this.checkForFileReady(medium);
            resolve(upload);
          } else {
            medium.uploading = undefined;
            return firstValueFrom(this.api.mm_Medium_deleteMedium(medium.id).pipe(take(1))).then(() => {
              medium = null;
              resolve(null);
              // this.requestDebounced();
              // this.resetFileUpload();
            });
          }
        })
        .catch(error => {
          medium.uploading = undefined;
          reject(error);
        })
        .finally(() => {
          this.medias = this.orderArrayOnElement(medium, this.medias);
          if (this.selectedFolder?.id) {
            this.getFolders();
          }
        });
    });
  }

  replaceMediumFile(medium: MediumDTO, file: File): Promise<MediumUploadDTO> {
    return new Promise((resolve, reject) => {
      const uploadDTO: MediumUploadDTO = medium as unknown as MediumUploadDTO;
      uploadDTO.uploading = true;
      uploadDTO.processing = undefined;
      this.uploadMediumFile(medium, file)
        .then((upload: MediumUploadDTO) => {
          uploadDTO.processing = true;
          uploadDTO.uploading = undefined;
          this.checkForFileReady(medium);
          resolve(upload);
        })
        .catch(error => {
          uploadDTO.uploading = undefined;
          reject(error);
        });
    });
  }

  async uploadMediumFile(medium: MediumDTO, file: File): Promise<MediumUploadDTO> {
    if (!medium?.id) {
      return Promise.reject(new Error('No Medium with ID was provided'));
    }
    try {
      const choice = await this.md5Check(file);
      if (!choice) {
        return Promise.resolve(null);
      }
      const singleFileUpload: SingleFileUpload = new SingleFileUpload();
      singleFileUpload.file = file;
      this.previewMedium = medium as MediumUploadDTOAndTags;
      try {
        const result = firstValueFrom(this.api.mm_Medium_uploadFile(medium.id, singleFileUpload).pipe(take(1)));
        return Promise.resolve(result);
      } catch (err) {
        return Promise.reject(err);
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  md5Check(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.util
        .computeChecksumMd5(file)
        .then(r => {
          this.api
            .mm_Medium_retrieveAll({ hashMD5: [r] })
            .pipe(take(1))
            .subscribe({
              next: data => {
                if (data.length > 0) {
                  this.modal
                    .confirm({
                      title: this.translate.instant('lang.globals.existingMotive'),
                      message: this.translate.instant('lang.globals.existingMotiveMessage'),
                      yes: this.translate.instant('lang.globals.yes'),
                      no: this.translate.instant('lang.globals.no'),
                    })
                    .then((result: boolean) => {
                      if (result) {
                        resolve(true);
                      } else {
                        resolve(false);
                      }
                    });
                } else {
                  resolve(true);
                }
              },
              error: error => {
                reject(error);
              },
            });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  orderArrayOnElement(medium: MediumUploadDTO, mediumArray: MediumUploadDTO[]): MediumUploadDTO[] {
    if (!medium) {
      return mediumArray;
    }
    if (mediumArray.length === 0) {
      return [medium];
    }
    const index = mediumArray.findIndex(med => med.id === medium.id);
    if (index === -1) {
      mediumArray.unshift(medium);
    } else {
      for (let i = index; i > 0; i--) {
        mediumArray[i] = mediumArray[i - 1];
      }
      mediumArray[0] = medium;
    }
    return mediumArray;
  }

  checkForFileReady(medium: any) {
    if (medium.processing) {
      setTimeout(() => {
        this.api
          .mm_Medium_retrieveById(medium.id, { meta: true })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              this.checkForFileReady(data);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }, 2500);
    } else {
      medium.processing = undefined;
      medium.uploading = undefined;

      if (this.previewMedium?.id === medium?.id) {
        this.previewMedium = medium;
      } else {
        this.previewMedium.processing = undefined;
        this.previewMedium.uploading = undefined;
      }
      this.medias = this.medias.map(x => (x?.id === medium?.id ? medium : x));
    }

    /*
    setTimeout(() => {
        medium.processing = undefined;
        medium.uploading = undefined;
        this.api
          .mm_Medium_retrieveById(medium.id, { meta: true })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              if (this.previewMedium?.id === data?.id) {
                this.previewMedium = data;
              } else {
                this.previewMedium.processing = undefined;
                this.previewMedium.uploading = undefined;
              }
              this.medias = this.medias.map(x => (x?.id === data?.id ? data : x));
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }, 5000);
     */
  }

  mediumToPreview(medium: MediumUploadDTOAndTagsAndButtons | null) {
    this.previewMedium = medium;
    this.toggleSideBar();
  }

  addNewMedium() {
    if (this.showSideBar) {
      this.showSideBar = false;
      this.previewMedium = null;
      setTimeout(() => {
        this.showSideBar = true;
      }, 200);
    } else {
      this.previewMedium = null;
      this.showSideBar = true;
    }
  }

  lockMedium(medium: MediumUploadDTO) {
    if (!medium?.id || medium.locked || !medium.uploaded) {
      return;
    }

    const title = this.translate.instant('lang.mediumExplorer.confirmSetReady');
    const message = this.translate.instant('lang.mediumExplorer.confirmSetReadyQuestion');
    const yes = this.translate.instant('lang.globals.yes');
    const no = this.translate.instant('lang.mediumExplorer.confirmSetReady.no');
    this.modal
      .confirm({
        title,
        message,
        yes,
        no,
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .mm_Medium_lockMedium(medium.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (response: MediumDTO) => {
                medium.locked = response.locked;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  onMediumNameChanged(medium: MediumFullDTO) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      if (medium.id) {
        this.api
          .mm_Medium_edit(medium)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (response: MediumFullDTO) => {
              medium.name = response.name;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }, 500);
  }

  async deleteMedium(medium: MediumUploadDTO) {
    if (!medium?.id) {
      return;
    }
    try {
      const result = await this.modal.confirm({
        title: this.translate.instant('lang.motives.deleteMotive'),
        message: this.translate.instant('lang.motives.deleteMotiveQuestion', { medium: medium?.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      });

      if (!result) {
        return;
      }
      this.api
        .mm_Medium_deleteMedium(medium.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.previewMedium = null;
            this.showSideBar = false;
            this.requestDebounced();
            this.getFolders();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } catch (err) {
      // modal error
    }
  }

  toggleMediumSelection(medium: MediumUploadDTO) {
    if (this.selectedMedias.has(medium.id)) {
      this.selectedMedias.delete(medium.id);
    } else {
      this.selectedMedias.set(medium.id, medium);
    }
    this.recalculateSelectionState();
  }

  returnMediumsToModal() {
    this.modalState.modals.get(this.isInModalContext).close(Array.from(this.selectedMedias.values()));
  }

  dismissModal() {
    this.modalState.modals.get(this.isInModalContext).dismiss();
  }

  addMediumToBooking(medium: MediumUploadDTO) {
    this.modalState.modals.get(this.isInModalContext).close(medium);
  }

  preSelectCompany() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: CompaniesComponent, data: { toggleContext: 'hasMedia' } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: CompanyDTO) => {
        if (result) {
          if (!result.id) {
            this.preselectedCompany = null;
          } else {
            this.preselectedCompany = result;
          }
        } else {
          this.preselectedCompany = null;
        }
      })
      .catch(() => {});
  }

  private createMultipleMediumsByFilesImpl(files: File[]): Promise<void> {
    return new Promise(resolve => {
      if (files?.length > 0) {
        const file: File = files.shift();
        this.createMediumByFile(file).finally(() => {
          this.createMultipleMediumsByFilesImpl(files).then(() => resolve());
        });
      } else {
        resolve();
      }
    });
  }

  /* dragging events */
  moveDragElement(event: DragEvent) {
    if (this.dragging) {
      this.dragItemStyle.left = event.pageX + 25;
      this.dragItemStyle.top = event.pageY - 50;
    }
  }

  dragStart($event: any) {
    const event: DragEvent = $event.event;
    this.dragging = $event.item;
    if (event.dataTransfer.setDragImage) {
      const dragDummy = (this.element.nativeElement as Element).querySelector('.drag-dummy');
      event.dataTransfer.setDragImage(dragDummy, 0, 0);
    }
    this.dragItemStyle = {
      left: event.screenX,
      top: event.screenY,
    };
  }

  dragEnd() {
    this.dragging = undefined;
  }

  dragOver(event: DragEvent, folderId: number) {
    if (this.dragging) {
      event.preventDefault();
      this.folderDragoverId = folderId;
    }
  }

  dragOut(event: DragEvent) {
    event.preventDefault();
    this.folderDragoverId = null;
  }

  drop(event: DragEvent, folder: MediumFolderDTO) {
    if (!this.dragging) {
      return;
    }
    if (this.selectedMedias.size > 1 && this.selectedMedias.has(this.dragging.id)) {
      this.moveSelectedToFolder(folder);
    } else {
      this.moveToFolder(folder, this.dragging);
    }
  }

  /* folder */

  doFolderSearch(searchObj: SearchType) {
    this.folderSearch = searchObj.searchTerm;
    this.exactMatchFolder = searchObj.exactMatch;
    this.getFolders();
  }

  getFolders() {
    this.api
      .mm_MediumFolder_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatchFolder, this.folderSearch),
        },
        this.folderPagination
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: MediumFolderDTO[]) => {
          if (!data) {
            data = [];
          }
          this.mediumFolders = data;
          this.folderString = this.util.getPaginationString(
            this.folderPagination.getValue().page,
            this.folderPagination.getValue().count,
            this.mediumFolders.length,
            this.folderPagination.getValue().resultcount,
            'folder'
          );
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  moveToFolder(folder: MediumFolderDTO, medium: MediumFullDTO) {
    medium.folder = folder;
    this.api
      .mm_Medium_edit(medium)
      .pipe(
        take(1),
        finalize(() => {
          this.dragging = undefined;
          this.folderDragoverId = null;
        })
      )
      .subscribe({
        next: () => {
          this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.itemMovedToFolder', { itemName: medium.name }));
          this.requestDebounced();
          this.getFolders();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  moveSelectedToFolder(folder: MediumFolderDTO) {
    if (this.selectedMedias?.size === 0) {
      return;
    }
    this.selectedMedias.forEach(medium => {
      medium.folder = folder;
    });
    this.api
      .mm_Medium_editMultiple(Array.from(this.selectedMedias.values()))
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.dragging = undefined;
          this.folderDragoverId = null;
        })
      )
      .subscribe({
        next: () => {
          this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.itemsMovedToFolder'));
          this.selectAction('deselectAll');
          this.requestDebounced();
          this.getFolders();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  removeFromFolder(folderId: number, medium: MediumDTO) {
    this.api
      .mm_MediumFolder_moveToRoot(medium.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.dragging = undefined;
          this.folderDragoverId = null;
        })
      )
      .subscribe({
        next: () => {
          this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.itemMovedToFolder', { itemName: medium.name }));
          this.requestDebounced();
          this.getFolders();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  toggleFolderCreation($event?: MouseEvent) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.folderCreationMode = !this.folderCreationMode;
    if (this.folderCreationMode && this.folderInput) {
      setTimeout(() => {
        this.folderInput.nativeElement.focus();
      }, 100);
    }
  }

  toggleFolder(folder: MediumFolderDTO | null, folderView: boolean = true) {
    this.selectedFolder = folder;
    this.folderView = folderView;
    this.requestDebounced();
  }

  createNewFolder() {
    if (!this.folderName) {
      return;
    }
    this.folderCreationInProgress = true;
    const folder: MediumFolderDTO = new MediumFolderDTO();
    folder.name = this.folderName;
    this.api
      .mm_MediumFolder_createRootFolder(folder)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.folderName = null;
          this.folderCreationMode = false;
          this.folderCreationInProgress = false;
          this.toggleFolderCreation();
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: data => {
          this.pushToFolders(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  deleteFolder($event: MouseEvent, folder: MediumFolderDTO) {
    $event.preventDefault();
    $event.stopPropagation();
    if (!folder) {
      return;
    }
    if (folder.elementsCount > 0) {
      this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.attachment.deleteFolderSizeWarning'));
      return;
    }
    if (folder.elementsCount === 0) {
      this.modal
        .confirm({
          title: this.translate.instant('lang.attachment.deleteFolder'),
          message: this.translate.instant('lang.attachment.deleteFolderMessage', { itemName: folder.name }),
          yes: this.translate.instant('lang.globals.yes'),
          no: this.translate.instant('lang.globals.no'),
        })
        .then((result: boolean) => {
          if (result) {
            this.api
              .mm_MediumFolder_deleteById(folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: () => {
                  this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.deleteSuccess'));
                  this.getFolders();
                },
              });
          }
        });
    }
  }

  pushToFolders(folder: MediumFolderDTO) {
    this.mediumFolders.unshift(folder);
    this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.successful'));
  }

  isEqualCompanyForSelected() {
    let isEqualCompany: boolean = true;
    if (this.selectedMedias.size <= 1) {
      return isEqualCompany;
    }
    const medias = Array.from(this.selectedMedias.values());
    const company: NamedIdentityDTO = medias[0]?.company;
    for (const medium of medias.slice(1)) {
      if (medium.company.id !== company.id) {
        isEqualCompany = false;
        return isEqualCompany;
      }
    }
    return isEqualCompany;
  }

  toMediumGroup() {
    if (this.selectedMedias?.size === 0) {
      return;
    }
    if (!this.isEqualCompanyForSelected()) {
      return;
    }
    if (this.hasMediumInMediumGroup()) {
      this.modal
        .confirm({
          title: this.translate.instant('lang.globals.confirmation'),
          message: this.translate.instant('lang.globals.moveMediumToMediumGroup'),
          yes: this.translate.instant('lang.globals.yes'),
          no: this.translate.instant('lang.globals.no'),
        })
        .then((result: boolean) => {
          if (result) {
            this.createMediumGroupForSelectedMedia();
          }
        });
    } else {
      this.createMediumGroupForSelectedMedia();
    }
  }

  private createMediumGroupForSelectedMedia() {
    const firstSelectedMedia = this.selectedMedias[0];
    const mediumGroup = new MediumGroupFullDTO();
    mediumGroup.name = firstSelectedMedia.name;
    this.api
      .mm_MediumGroup_create(firstSelectedMedia.company.id, mediumGroup)
      .pipe(
        switchMap((group: MediumGroupFullDTO) => this.api.mm_MediumGroup_addMedia(group.id, Array.from(this.selectedMedias.values()))),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe({
        next: (filledMediumGroup: MediumGroupFullDTO) => {
          this.navService.navigateToPoint(this.util.RoutingID.mediumGroupEdit, [
            {
              id: 'mediumGroupID',
              value: filledMediumGroup.id,
            },
          ]);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  setReady() {
    const mediaIDs: number[] = Array.from(this.selectedMedias.values())
      .filter(x => !x.locked)
      .map(x => x.id);
    this.api
      .mm_Medium_lockMedia({ medium_id: mediaIDs })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: () => {
          this.selectedMedias.forEach(medium => (medium.locked = true));
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  async deleteMultiple() {
    try {
      const medias: MediumUploadDTOAndTagsAndButtons[] = Array.from(this.selectedMedias.values());
      const mediaName = medias.filter(medium => !medium.locked).map(medium => medium.name);
      if (mediaName.length === 0) {
        return;
      }
      const result = await this.modal.confirm({
        title: this.translate.instant('lang.motives.deleteMotive'),
        message: this.translate.instant('lang.motives.deleteMotiveQuestion', { medium: mediaName.join(', ') }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      });

      if (!result) {
        return;
      }

      const deleteRequests = medias
        .filter(x => !x.locked)
        .map(x => x.id)
        .map(id => this.api.mm_Medium_deleteMedium(id));

      if (!deleteRequests.length) {
        return;
      }

      forkJoin(deleteRequests)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.requestDebounced();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } catch (err) {}
  }

  changeCompany() {
    if (!this.isLockedMediumInSelected()) {
      return;
    }
    if (this.hasMediumInMediumGroup()) {
      return;
    }
    this.modal
      .showNavigationModal({
        entryComponent: { value: CompaniesComponent, data: {} },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: CompanyDTO) => {
        if (result) {
          const updatedArray = Array.from(this.selectedMedias.values());
          updatedArray.forEach(medium => {
            medium.company = result;
          });
          this.api
            .mm_Medium_editMultiple(updatedArray)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.requestDebounced();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        } else {
          this.customer = null;
        }
      })
      .catch(() => {});
  }

  isLockedMediumInSelected(): boolean {
    return Array.from(this.selectedMedias.values()).filter(x => x.locked).length === 0;
  }

  hasMediumInMediumGroup() {
    return Array.from(this.selectedMedias.values()).filter(x => x.mediumGroup).length > 0;
  }

  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  private recalculateSelectionState() {
    if (!this.medias || this.medias.length === 0) {
      this.selectionState = 'none';
      return;
    }
    const allSelectedOnCurrentPage = this.medias.every(medium => this.selectedMedias.has(medium.id));
    if (allSelectedOnCurrentPage) {
      this.selectionState = 'all';
      return;
    }
    const someSelectedOnCurrentPage = this.medias.some(medium => this.selectedMedias.has(medium.id));
    if (someSelectedOnCurrentPage) {
      this.selectionState = 'partly';
      return;
    }
    this.selectionState = 'none';
  }

  selectAction($event: MultiSelectButtonAction) {
    if (!this.medias || this.medias.length === 0) {
      return;
    }
    switch ($event) {
      case 'selectPage':
        this.medias.forEach(medium => {
          if (!medium['excluded']) {
            this.selectedMedias.set(medium.id, medium);
          }
        });
        this.selectionState = 'all';
        break;
      case 'deselectAll':
        this.selectedMedias.clear();
        this.selectionState = 'none';
        break;
      case 'deselectPage':
      default:
        this.medias.forEach(medium => {
          this.selectedMedias.delete(medium.id);
        });
        this.selectionState = 'none';
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.locked = filterMap.get('locked')?.[0];
    this.mediumGroup = filterMap.get('mediumGroupSet')?.[0];
    this.selectedCompany = filterMap.get('companies')?.[0];
    this.resolution = this.resolutionDisabled ? this.resolution : filterMap.get('resolution')?.[0];
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.orderOption.set({ ...this.filterPanelOrderOption() });
  }

  displayNoMatchingMarker(medium: MediumUploadDTOAndTagsAndButtons): boolean {
    if (!medium || !medium.resolution || !this.resolutionOptions || this.resolutionOptions.length <= 0) {
      return true;
    }
    return !this.resolutionOptions.some(resolution => resolution.id === medium.resolution.id);
  }
}
